import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkle, CaretDown, CaretRight, MagnifyingGlass, FileText, X, Check, ArrowClockwise,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { devApi } from './devApi';
import { CitationList, ConfidenceBadge } from './Citations';

const DMS = `${process.env.REACT_APP_BACKEND_URL}/api/dms`;

/**
 * AIワークスペース — 書類から見直す。
 * 案件にリンク済みの書類 + 追加で選んだ書類 + メモを材料に、構想提案の更新案を提案する。
 * 自動保存は一切しない。反映は項目ごとに担当者が行う。AIは常に補助（主操作にしない）。
 */
export const AiWorkspace = ({ devId, linkedDocs, concept, canEdit, onApplied }) => {
  const [open, setOpen] = useState(false);
  const [useLinked, setUseLinked] = useState(true);
  const [notes, setNotes] = useState('');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState({});

  const search = async (v) => {
    setQ(v);
    if (!v.trim()) { setHits([]); return; }
    try {
      const r = await axios.get(`${DMS}/documents`, { params: { q: v, limit: 8 } });
      setHits(r.data || []);
    } catch (_e) { setHits([]); }
  };

  const pick = (d) => {
    if (!picked.some((p) => p.id === d.id)) setPicked([...picked, d]);
    setQ(''); setHits([]);
  };

  const refine = async () => {
    setBusy(true);
    setApplied({});
    try {
      const r = await devApi.aiRefine(devId, {
        notes,
        document_ids: picked.map((p) => p.id),
        include_linked: useLinked,
      });
      setResult(r);
      if (r.note) toast.error(r.note);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AIの見直し案を生成できませんでした');
    }
    setBusy(false);
  };

  const apply = async (row) => {
    try {
      await devApi.patch(devId, {
        concept: { ...concept, [row.key]: row.proposed },
        concept_note: 'AIワークスペースの提案を反映',
      });
      setApplied((a) => ({ ...a, [row.key]: true }));
      toast.success(`「${row.label}」を更新しました`);
      onApplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || '反映に失敗しました');
    }
  };

  const changedRows = (result?.fields || []).filter((f) => f.changed);
  const unchangedRows = (result?.fields || []).filter((f) => !f.changed);

  return (
    <section className="rounded-lg border border-zinc-300 bg-white shadow-sm" data-testid="dev-ai-workspace">
      <button type="button" onClick={() => setOpen(!open)} data-testid="dev-ai-workspace-toggle"
        className="flex w-full items-center gap-2 border-b border-zinc-200 bg-zinc-100 px-4 py-2.5 text-left hover:bg-zinc-200/60">
        {open ? <CaretDown size={14} className="text-zinc-600" /> : <CaretRight size={14} className="text-zinc-600" />}
        <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-800">
          <Sparkle size={15} weight="duotone" className="text-teal-700" /> AIワークスペース — 書類から見直す
        </p>
        <p className="hidden text-xs text-zinc-600 sm:block">
          新しい図面・試験報告書・提案書から、構想提案の更新案を出典付きで提案します。自動保存はされません。
        </p>
      </button>

      {open && (
        <div className="space-y-4 p-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-800">
              <Checkbox checked={useLinked} onCheckedChange={(v) => setUseLinked(!!v)}
                data-testid="dev-ai-refine-use-linked" />
              この案件にリンク済みの書類を材料にする（{linkedDocs.length}件）
            </label>
            {useLinked && linkedDocs.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {linkedDocs.map((d) => (
                  <span key={d.document_id}
                    className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-zinc-50 px-2 py-1 text-xs text-zinc-800">
                    <FileText size={12} /> {d.title}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">追加で参照する書類（任意）</Label>
            <div className="relative w-64">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-zinc-500" />
              <Input value={q} onChange={(e) => search(e.target.value)} className="pl-8"
                placeholder="ドキュメントアプリから探す" data-testid="dev-ai-refine-doc-search" />
            </div>
            {hits.length > 0 && (
              <div className="mt-2 max-h-44 divide-y divide-zinc-200 overflow-y-auto rounded-md border border-zinc-300">
                {hits.map((d) => (
                  <button key={d.id} type="button" onClick={() => pick(d)}
                    data-testid={`dev-ai-refine-doc-option-${d.id}`}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-teal-50/60">
                    <FileText size={14} className="shrink-0 text-zinc-500" />
                    <span className="min-w-0 flex-1 truncate text-sm text-zinc-900">
                      {d.document_number ? `${d.document_number} · ` : ''}{d.title}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {picked.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dev-ai-refine-picked">
                {picked.map((d) => (
                  <span key={d.id}
                    className="inline-flex items-center gap-1 rounded-md border border-teal-300 bg-teal-50 px-2 py-1 text-xs font-medium text-teal-900">
                    <FileText size={12} /> {d.title}
                    <button type="button" onClick={() => setPicked(picked.filter((p) => p.id !== d.id))}
                      aria-label="外す" className="text-teal-700 hover:text-rose-600">
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">メモ（任意）</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="例: 試作1号の検証で温度ムラが±3℃に改善。目標仕様を見直したい。"
              data-testid="dev-ai-refine-notes" />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={refine} disabled={busy || !canEdit
              || (!useLinked && picked.length === 0 && !notes.trim())}
              data-testid="dev-ai-refine-btn">
              <Sparkle size={14} className="mr-1" /> {busy ? '見直し中…' : '書類から見直す'}
            </Button>
            {result && (
              <Button variant="outline" onClick={refine} disabled={busy} data-testid="dev-ai-refine-again-btn">
                <ArrowClockwise size={14} className="mr-1" /> もう一度
              </Button>
            )}
          </div>

          {result && (
            <div className="space-y-3 rounded-lg border border-teal-300 bg-teal-50/40 p-4" data-testid="dev-ai-refine-result">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-bold text-zinc-900">
                  AIの見直し案（変更提案 {changedRows.length}件）
                </p>
                <p className="text-xs font-semibold text-teal-900">{result.disclaimer}</p>
              </div>
              {(result.sources || []).length > 0 && (
                <p className="text-xs text-zinc-700" data-testid="dev-ai-refine-sources">
                  参照した書類: {result.sources.map((s) => `${s.title}（${s.used ? `${s.chars}文字` : '文字なし'}）`).join('、')}
                </p>
              )}
              {result.note && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900"
                  data-testid="dev-ai-refine-note">{result.note}</p>
              )}
              {!result.note && changedRows.length === 0 && (
                <p className="text-sm text-zinc-700" data-testid="dev-ai-refine-nochange">
                  書類から根拠のある変更提案はありませんでした。
                </p>
              )}
              {changedRows.map((f) => (
                <div key={f.key} className="rounded-md border border-zinc-300 bg-white p-3"
                  data-testid={`dev-ai-refine-field-${f.key}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs font-bold text-zinc-800">{f.label}</p>
                    <StatusBadge tone="warning" testid={`dev-ai-refine-changed-${f.key}`}>変更提案</StatusBadge>
                    <ConfidenceBadge level={f.confidence} testid={`dev-ai-refine-conf-${f.key}`} />
                    {applied[f.key] ? (
                      <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                        <Check size={13} /> 反映済み
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" className="ml-auto h-7 px-2 text-xs"
                        disabled={!canEdit} onClick={() => apply(f)}
                        data-testid={`dev-ai-refine-apply-${f.key}`}>この内容に更新</Button>
                    )}
                  </div>
                  {f.reason && <p className="mt-1 text-xs text-zinc-600">理由: {f.reason}</p>}
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-semibold text-zinc-500">現在</p>
                      <p className="whitespace-pre-wrap text-sm text-zinc-500">{f.current || '（未入力）'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-teal-800">提案</p>
                      <p className="whitespace-pre-wrap text-sm text-zinc-900">{f.proposed}</p>
                    </div>
                  </div>
                  <CitationList citations={f.citations} testid={`dev-ai-refine-citations-${f.key}`} />
                </div>
              ))}
              {unchangedRows.length > 0 && (
                <p className="text-xs text-zinc-600" data-testid="dev-ai-refine-unchanged">
                  変更なし: {unchangedRows.map((f) => f.label).join('、')}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
